import ExcelJS from 'exceljs'

export interface ProcRequestItem {
  requestItemId?: string
  clientCode?: string | null
  requestNumber?: string | null
  partNumber: string
  brand: string | null
  quantity: number | string
  comment: string | null
}

export interface ProcRequestOptions {
  clientCode: string | null
  requestNumber: string
  items: ProcRequestItem[]
  isBatch?: boolean
  // Имя поставщика — заполняется в столбце Supplier для каждой строки.
  // Используется при генерации файла «Запрос поставщику» (новая фича 2026-05-07).
  supplierName?: string | null
  // Уникальный код запроса (`STRONG-20260507-001`) — пишется в свойства
  // документа и используется в имени файла, чтобы при загрузке ответа
  // парсер автоматически нашёл к какому запросу относится файл.
  fileCode?: string | null
  // Произвольное имя файла без расширения. Используется для скачивания
  // «без поставщика» (имя «ноунейм») — когда ProcurementRequest не создаётся
  // и fileCode отсутствует.
  fileNameOverride?: string | null
}

export async function generateProcurementRequest(opts: ProcRequestOptions): Promise<void> {
  const wb = new ExcelJS.Workbook()
  // fileCode — пишем в core-properties документа (title + subject).
  // Закупщик не видит, поставщик не трогает. Парсер ответа читает при
  // загрузке Excel и сразу понимает к какому ProcurementRequest относится
  // файл (поле subject — `procurement-request:STRONG-20260507-001`).
  if (opts.fileCode) {
    wb.title = opts.fileCode
    wb.subject = `procurement-request:${opts.fileCode}`
  }
  // Имя листа — латиницей: запросы уходят китайским/международным
  // поставщикам, кириллица в табе листа на их Excel может ломаться. Парсер
  // ответа берёт первый лист по индексу (worksheets[0]), а не по имени —
  // старые файлы со старым названием листа подгружаются корректно.
  const ws = wb.addWorksheet('Request')

  const batch = !!opts.isBatch

  // Формат с 15.05.2026: после Price добавлен столбец Currency (валюта
  // квоты — EUR/CNY/USD, пусто = CNY), Note и Supplier comment поменяны
  // местами. Парсер отличает новый файл от старого по заголовку «Currency».
  const columns = [
    { key: 'n', width: 5 },
    ...(batch ? [{ key: 'requestItemId', width: 20 }] : []),
    ...(batch ? [{ key: 'clientCode', width: 15 }] : []),
    ...(batch ? [{ key: 'requestNumber', width: 15 }] : []),
    { key: 'supplier', width: 25 },
    { key: 'partNumber', width: 25 },
    { key: 'brand', width: 18 },
    { key: 'qty', width: 12 },
    { key: 'price', width: 15 },
    { key: 'currency', width: 12 },
    { key: 'supplierComment', width: 35 },
    { key: 'note', width: 35 },
  ]
  ws.columns = columns

  const border: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'FF000000' } },
    left: { style: 'thin', color: { argb: 'FF000000' } },
    bottom: { style: 'thin', color: { argb: 'FF000000' } },
    right: { style: 'thin', color: { argb: 'FF000000' } },
  }

  const headerFill: ExcelJS.Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD9E1F2' },
  }

  const headerFont: Partial<ExcelJS.Font> = {
    name: 'Times New Roman',
    size: 10,
    bold: true,
  }

  const bodyFont: Partial<ExcelJS.Font> = {
    name: 'Times New Roman',
    size: 10,
  }

  const headers = [
    '№',
    ...(batch ? ['ID позиции'] : []),
    ...(batch ? ['Код клиента'] : []),
    ...(batch ? ['Заявка'] : []),
    'Supplier',
    'Part number',
    'Brand',
    'Quantity',
    'Price',
    'Currency',
    'Supplier comment',
    'Note',
  ]

  const headerRow = ws.getRow(1)
  headers.forEach((h, i) => {
    const c = headerRow.getCell(i + 1)
    c.value = h
    c.font = headerFont
    c.fill = headerFill
    c.border = border
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  })
  headerRow.height = 30

  // Данные
  for (let i = 0; i < opts.items.length; i++) {
    const item = opts.items[i]
    const row = ws.getRow(i + 2)
    row.height = 20

    // Порядок vals: …, Quantity, Price(''), Currency(''), Supplier comment(''),
    // Note(item.comment). Note автоподтягивается из CRM (заметка менеджера) —
    // закупщику не нужно переписывать руками. Price/Currency/Supplier comment
    // пустые — их заполняет поставщик. Currency пусто = CNY при импорте.
    const vals: (string | number)[] = [
      i + 1,
      ...(batch ? [item.requestItemId ?? ''] : []),
      ...(batch ? [item.clientCode ?? ''] : []),
      ...(batch ? [item.requestNumber ?? ''] : []),
      // Supplier-колонка: для запроса поставщику предзаполняем именем
      // (поставщик его не правит, мы видим в импорте кому что отправляли).
      // Для старого batch-режима остаётся пустой — поставщик заполнит сам.
      opts.supplierName ?? '',
      item.partNumber,
      item.brand ?? '',
      Number(item.quantity),
      '',
      '',
      '',
      item.comment ?? '',
    ]

    // Индекс столбца Price: после служебных скрытых колонок. Столбцы
    // [priceColIdx … конец] поставщик заполняет — их разлочиваем при защите.
    const priceColIdx = batch ? 9 : 6

    vals.forEach((v, j) => {
      const c = row.getCell(j + 1)
      c.value = v
      c.font = bodyFont
      c.border = border
      c.alignment = {
        vertical: 'middle',
        wrapText: true,
      }
      // Защита: столбцы 1..(priceColIdx-1) залочены (служебные + данные
      // позиции), Price и правее — разлочены для ввода поставщиком.
      c.protection = { locked: j + 1 < priceColIdx }
    })
  }

  if (batch) {
    // Скрываем служебные столбцы (ID позиции, Код клиента, Заявка) — колонки 2, 3, 4
    ws.getColumn(2).hidden = true
    ws.getColumn(3).hidden = true
    ws.getColumn(4).hidden = true
  }

  // Частичная защита листа (с 15.05.2026): столбцы 1-8 (служебные + данные
  // позиции) залочены от изменений, Price и правее — разлочены для ввода
  // поставщиком. selectLockedCells: true оставляет защищённые ячейки
  // доступными для выделения и копирования (Ctrl+C).
  // ВНИМАНИЕ: 05.05 защиту убирали — Mac Excel при protected sheet мешал
  // Cmd+F → «Заменить всё». Сейчас Price (массовая замена точки на запятую)
  // в разлоченной зоне, и блокировки замены в выделенном фрагменте быть
  // не должно. Если у закупщиков замена снова сломается — снять защиту.
  await ws.protect('', {
    selectLockedCells: true,
    selectUnlockedCells: true,
  })

  // Скачивание
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')

  // Имя файла: для запроса поставщику — fileCode (`STRONG-20260507-001.xlsx`),
  // чтобы закупщик и поставщик в WeChat сразу видели какому запросу принадлежит.
  // Для старого batch-режима — клиент-код + номер заявки.
  const code = opts.clientCode ?? 'NO_CODE'
  const fileName = opts.fileNameOverride
    ? `${opts.fileNameOverride}.xlsx`
    : opts.fileCode
      ? `${opts.fileCode}.xlsx`
      : `${code} - ${opts.requestNumber}.xlsx`
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}
